import { PiCaretLeftBold, PiCaretRightBold } from "react-icons/pi"
import { useDispatch } from "react-redux"
import { Link, useLocation } from "react-router-dom"
import { Button, cn, Text, Tooltip } from "rizzui"
import { useBerylliumSidebars } from "../../hooks/themes/berylliumUtils"
import { LAYOUT_OPTIONS, useLayout } from "../../hooks/useLayout"
import { useScrollableSlider } from "../../hooks/useScrollableSlider"
import { permissionsGroup, usePrivilege } from "../priviledge/PriviledgeProvider"
import { MdError } from "react-icons/md"

export type MenuItemInside = {
    id: string
    label: string
    icon?: React.ReactNode
    permission: {
        action: string
        group: permissionsGroup
    }
    fieldsError?:string[]
}

export type TabNavInsideProps = {
    menuItems: MenuItemInside[]
    setSelectedView?: any
    selectedView?: string
    errors?:any
}

export default function TabNavInside({ menuItems, setSelectedView, selectedView, errors }: TabNavInsideProps) {

    // HOOKS

    const { sliderEl, sliderPrevBtn, sliderNextBtn, scrollToTheRight, scrollToTheLeft } = useScrollableSlider();
    const { layout } = useLayout();
    const { expandedLeft } = useBerylliumSidebars();
    const dispatch = useDispatch();
    const { userCan } = usePrivilege();

    // RENDER

    return (
        <div
            className={cn(
                'max-w-[99vw] -mx-4 -mt-4 px-4 py-0 font-medium text-gray-500 sm:-mt-2 md:-mx-5 md:px-5 lg:-mx-8 lg:mt-0 lg:px-8 xl:-mx-6 xl:px-6 2xl:top-20 3xl:-mx-[33px] 3xl:px-[33px] 4xl:-mx-10 4xl:px-10 dark:bg-gray-50',
                layout === LAYOUT_OPTIONS.LITHIUM
                    ? 'top-[66px] sm:top-[70px] md:top-[73px] '
                    : layout === LAYOUT_OPTIONS.BERYLLIUM
                        ? 'top-[62px] sm:top-[72px] 2xl:top-[72px]'
                        : 'top-[62px] md:top-[71px]',
                layout === LAYOUT_OPTIONS.BERYLLIUM && expandedLeft && 'xl:-ms-1 xl:px-0 3xl:-ms-2 3xl:ps-0 4xl:-ms-2'
            )}
        >
            <div className="relative flex items-center overflow-hidden justify-center">
                <Button
                    title="Anterior"
                    variant="text"
                    ref={sliderPrevBtn}
                    onClick={() => scrollToTheLeft()}
                    className="!absolute left-0 top-0.5 z-10 !h-[calc(100%-4px)] w-8 !justify-start bg-gradient-to-r from-white via-white to-transparent px-0 text-gray-500 hover:text-black lg:hidden dark:from-gray-50 dark:via-gray-50"
                >
                    <PiCaretLeftBold className="w-5" />
                </Button>
                <div className="flex h-[52px] items-start overflow-hidden">
                    <div
                        className="-mb-7 flex w-full gap-3 overflow-x-auto scroll-smooth pb-7 md:gap-5 lg:gap-8"
                        ref={sliderEl}
                    >
                        {menuItems.map((menu: MenuItemInside, index: number) => {
                            if (menu.permission && !userCan(menu.permission.action, menu.permission.group)) return;
                            return (
                                <Button
                                    key={`menu-${index}`}
                                    className={cn(
                                        'flex justify-center items-center group relative cursor-pointer rign-color-transparent whitespace-nowrap py-1.5 font-medium bg-trasparent text-gray-500 before:absolute before:bottom-0 before:left-0 before:z-[1] before:h-0.5 before:transition-all',
                                        menu.id.toLowerCase() === selectedView?.toLowerCase() && 'text-primary border-0 border-b-4 border-black rounded',
                                    )}
                                    onClick={() => setSelectedView(menu.id)}
                                >
                                    {menu.icon && (
                                        <span className="me-1 inline-flex">
                                            {menu.icon}
                                        </span>
                                    )}
                                    <Text
                                        as="span"
                                        className="inline-flex rounded-md px-2.5 py-1.5 transition-all duration-200 flex items-center text-[12px]"
                                    >
                                        {menu.label}
                                    </Text>
                                    {errors && menu.fieldsError && menu.fieldsError.some(field => errors[field]!== undefined) && (   
                                        <Tooltip content="Errores en el formulario" placement="top">
                                            <span className="text-red-500">
                                                <MdError className="h-4 w-4" />
                                            </span>
                                        </Tooltip>
                                    )}
                                </Button>
                            );
                        })}
                    </div>
                </div>
                <Button
                    title="Siguiente"
                    variant="text"
                    ref={sliderNextBtn}
                    onClick={() => scrollToTheRight()}
                    className="!absolute right-0 top-0.5 z-10 !h-[calc(100%-4px)] w-8 !justify-end bg-gradient-to-l from-white via-white to-transparent px-0 text-gray-500 hover:text-black lg:hidden dark:from-gray-50 dark:via-gray-50"
                >
                    <PiCaretRightBold className="w-5" />
                </Button>
            </div>
            <hr className="my-2" />
        </div>
    )
}